// @ts-ignore
import SockJS from 'sockjs-client';
import Stomp from 'webstomp-client';

import { eventChannel, EventChannel } from 'redux-saga';
import {
  call,
  take,
  put,
  select,
  takeLatest,
  takeEvery,
  all,
  delay,
  cancelled,
} from 'redux-saga/effects';

import * as Sentry from '@sentry/react';

import * as ConfigAPI from '../config/api';
import { INIT_START } from '../actions/cabinet/CabinetActionsTypes';
import {
  getAccessToken,
  getRefreshToken,
  setTokens,
} from '../helpers/tokensHelpers';
import {
  setSocketConnectStatus,
  socketEventReserveConfirmation,
  socketEventReserveCreated, socketReconnect,
} from '../actions/webSocketService/WebSocketActions';
// import { customSnackBart } from '../actions/snackbars/SnackBarsActions';
import { getBookingList } from '../actions/booking/BookingActions';

import { IAppStore } from '../types/IAppStore';
import { IInitStarting } from '../actions/cabinet/ICabinetActions';

import {
  changePremiumBonusStatus,
  getCashBoxConnectStatus,
  getCashBoxPluginsStatus,
} from '../actions/cabinet/CabinetActions';
import { connectRestaurantSuccess } from '../actions/home/HomeActions';

import { addOpenReserve, removeOpenReserve } from '../actions/reservationsPage/ReservationsPageActions';

import {
  SOCKET_RECONNECT,
  SOCKET_SEND_MESSAGE,
  SOCKET_SUBSCRIBE_MESSENGER_AUTH,
  SOCKET_UNSUBSCRIBE_MESSENGER_AUTH,
} from '../actions/webSocketService/WebSocketActionsTypes';
import * as Api from '../api';
import { signOut } from '../actions/auth/AuthActions';
import { newNotification } from '../actions/notification/NotificationActions';

import { addRestaurantCall, removeRestaurantCall } from '../actions/restaurantsCalls/RestaurantsCallsActions';
import { IRestaurantCall } from '../types/IRestaurantCallsStore';
import { outerChangeTypeAppReserve } from '../actions/appReserves/AppReservesActions';

import { queryWithRetry } from '../api/utils';
import { selectorRestaurant, selectorRestaurantId } from '../selectors/RestaurantSelectors';
import { selectorIsAuthorized } from '../selectors/AuthSelectors';
import {
  scrapeRestaurantInfoError,
  scrapeRestaurantInfoSuccess,
} from '../actions/restaurantsInfo/RestaurantsInfoActions';
import {
  setRestaurantWhatsappState,
  setRestaurantTgState,
} from '../actions/guestmeNotifications/GuestmeNotificationsActions';
import {
  SET_WHATSAPP_RESTAURANT_STATE,
  SET_TG_RESTAURANT_STATE,
} from '../actions/guestmeNotifications/GuestmeNotificationsActionsTypes';
import { refetchActiveBookingsQueries } from './effects/refetchActiveBookings';
import { getGuestPremiumBonus } from '../_FSD/pages/loyalty/lib/premiumBonus';
import store from '../redux';

const websocketEmitterType = {
  error: 'ERROR',
  close: 'CLOSE',
  connect: 'CONNECT',
  connectRestaurant: 'CONNECT_RESTAURANT',

  // Резервы
  newReserve: 'NEW_RESERVE',
  confirmReserve: 'CONFIRM_RESERVE',
  update: 'UPDATE',

  newCall: 'NEW_CALL',
  callEnded: 'CALL_ENDED',

  restaurantScrapeInfo: 'RESTAURANT_SCRAPE_INFO',

  premiumBonusStatus: 'PREMIUN_BONUS_STATUS'
};

const websocketError = {
  invalidToken: 'invalid_token',
  unknownError: 'unknown_error',
};

let stompClient: any = null;
const MESSENGER_AUTH_TOPIC = '/user/event/restaurant/messenger/auth';
let messengerAuthSub: any = null;
let wantMessengerAuth = false;
let socketEmitterRef: any = null;

const guestmeNotificationsGatewayCallback = (message: any, emitter: any) => {
  if (!message?.body) {
    return;
  }

  const data: GM.GuestmeNotifications.Response.SocketMessage = JSON.parse(message.body);

  if (data.messenger === 'WHATSAPP') {
    emitter({
      type: SET_WHATSAPP_RESTAURANT_STATE,
      payload: data,
    });
  } else if (data.messenger === 'TELEGRAM') {
    emitter({
      type: SET_TG_RESTAURANT_STATE,
      payload: data,
    });
  }
};

const getReservePayloadWithPremiumBonus = async (reserveResponseMessageBody: string) => {
  const json = JSON.parse(reserveResponseMessageBody);
  let payload = { ...json };
  const phone = json.phone && Number(json.phone);

  if (phone) {
    try {
      const restaurantId = selectorRestaurantId(store.getState());
      const response = await getGuestPremiumBonus(restaurantId, phone);

      return {
        ...payload,
        ...response.data,
      }
    } catch (e) {
      return payload;
    }
  }
};

function createSocketChannel() {
  const subscribe = (emitter: any) => {
    socketEmitterRef = emitter;

    const ws = new SockJS(ConfigAPI.websocket, null, { transports: ['websocket'] });
    const token = getAccessToken();
    stompClient = Stomp.over(ws, { debug: false });

    ws.onclose = emitter({
      type: websocketEmitterType.close,
    });

    const connectCallback = () => {
      emitter({
        type: websocketEmitterType.connect,
      });

      const reservationCreatedCallback = async (message: any) => {
        if (!message.body) {
          return;
        }

        emitter({
          type: websocketEmitterType.newReserve,
          payload: await getReservePayloadWithPremiumBonus(message.body),
        });
      }

      stompClient.subscribe('/user/event/restaurant/reservation/create', reservationCreatedCallback, {});

      const confirmationRequestCallback = async (message: any) => {
        if (!message.body) {
          return;
        }

        emitter({
          type: websocketEmitterType.confirmReserve,
          payload: await getReservePayloadWithPremiumBonus(message.body),
        });
      };

      stompClient.subscribe('/user/event/restaurant/reservation/confirmation', confirmationRequestCallback, {});

      const restaurantScrapeInfoCallback = (message: any) => {
        if (message.body) {
          const json = JSON.parse(message.body);

          emitter({
            type: websocketEmitterType.restaurantScrapeInfo,
            payload: json,
          });
        }
      };

      stompClient.subscribe('/user/event/restaurant/scraping/event', restaurantScrapeInfoCallback);

      const updateBookingListCallback = (message: any) => {
        if (message.body) {
          const json = JSON.parse(message.body);

          emitter({
            type: websocketEmitterType.update,
            payload: json,
          });
        }
      };

      stompClient.subscribe('/user/event/restaurant/reservation/update', updateBookingListCallback, {});

      const connectRestaurantCallback = (message: any) => {
        if (message.body) {
          const json = JSON.parse(message.body);

          emitter({
            type: websocketEmitterType.connectRestaurant,
            payload: json,
          });
        }
      };

      stompClient.subscribe('/user/event/restaurant/registered/cashbox', connectRestaurantCallback);

      // Call
      // @todo Может перенести разбор статусов в сагу, как для резервов или резервы сюда?

      const connectRestaurantCallsCallback = (message: any) => {
        if (message.body) {
          const call: IRestaurantCall = JSON.parse(message.body);
          let emitterType: string | null = null;

          switch (call.state) {
            case 'ACTIVE':
            case 'ALERTING':
              emitterType = websocketEmitterType.newCall;
              break;

            case 'MISSED':
            case 'COMPLETED':
              emitterType = websocketEmitterType.callEnded;
              break;

            default:
              Sentry.captureMessage(`Unknown call state "${call.state}"`);
              console.log(`Unknown call state "${call.state}"`);
              break;
          }

          if (emitterType) {
            emitter({
              type: emitterType,
              payload: call,
            });
          }
        }
      };

      stompClient.subscribe('/user/event/restaurant/call', connectRestaurantCallsCallback);

      stompClient.subscribe('/user/event/restaurant/premium-bonus/status', (message: any) => {
        emitter({
          type: websocketEmitterType.premiumBonusStatus,
          payload: JSON.parse(message.body).status,
        });
      });


      if (wantMessengerAuth && !messengerAuthSub) {
        messengerAuthSub = stompClient.subscribe(
          MESSENGER_AUTH_TOPIC,
          (msg: any) => guestmeNotificationsGatewayCallback(msg, emitter),
        );
      }
    };

    const errorCallback = (error: any) => {
      const isInvalidToken = error.headers && error.headers.message.search(websocketError.invalidToken);
      emitter({
        type: websocketEmitterType.error,
        error: isInvalidToken ? websocketError.invalidToken : websocketError.unknownError,
        payload: error,
      });
    };

    stompClient.connect({ Authorization: `Bearer ${token}` }, connectCallback, errorCallback);

    return () => stompClient.disconnect();
  };

  return eventChannel(subscribe);
}

function* watchChannelEvents<T>(channel: EventChannel<T>) {
  while (true) {
    const event = yield take(channel);

    console.info(`[${new Date().toLocaleTimeString()}] ---socket_event---`, event);

    const currentRestaurant = yield select(selectorRestaurant);
    const currentRestaurantId = yield select(selectorRestaurantId);

    const date = yield select(
      (state: IAppStore) => state.booking.currentStatisticDate,
    );

    switch (event.type) {
      case websocketEmitterType.connect:
        yield put(setSocketConnectStatus(true));
        break;

      case websocketEmitterType.close:
        handleUnsubscribeMessengerAuth();

        yield put(setSocketConnectStatus(false));
        channel.close();
        return false;

      case websocketEmitterType.error:
        handleUnsubscribeMessengerAuth();

        if (event.error === websocketError.invalidToken) {
          // console.warn(`[${new Date().toLocaleTimeString()}] Неверный токен в веб-сокете, пробуем обновить токен`);
          const refreshToken = getRefreshToken();
          if (refreshToken) {
            try {
              const res = yield Api.refreshTokenReq(refreshToken);
              if (res.status === 200) {
                setTokens(res.data.access_token, res.data.refresh_token);
              } else {
                // console.warn(`[${new Date().toLocaleTimeString()}] Не 200 статус при обновлении токена, выходим из аккаунта`);
                yield put(signOut());
              }
            } catch (e) {
              // console.warn(`[${new Date().toLocaleTimeString()}] Исключение при обновлении токена веб-сокета`, e);
              yield put(signOut());
            }
          } else {
            // console.warn(`[${new Date().toLocaleTimeString()}] Нет refresh-токена, выходим из аккаунта`);
            yield put(signOut());
          }
        }

        yield put(setSocketConnectStatus(false));
        channel.close();
        return false;

      case websocketEmitterType.connectRestaurant:
        yield put(connectRestaurantSuccess());
        yield put(getCashBoxConnectStatus(currentRestaurantId));
        yield put(getCashBoxPluginsStatus(currentRestaurantId));
        break;

      case websocketEmitterType.confirmReserve:
        // Когда поступает новый резерв через виджет

        yield put(newNotification('reserve_confirmation'));
        yield put(socketEventReserveConfirmation());
        yield put(addOpenReserve(event.payload));

        break;

      case websocketEmitterType.newReserve:
        // yield put(customSnackBart({
        //   message: 'Создан новый резерв',
        // }));

        yield put(socketEventReserveCreated());

        yield call(refetchActiveBookingsQueries, {
          type: `WEBSOCKET_${event.type}`,
          date,
          event,
        });

        break;

      case websocketEmitterType.restaurantScrapeInfo:
        if (event.payload.status === 'ERROR') {
          yield put(scrapeRestaurantInfoError())
          break;
        }

        if (event.payload.status === 'SUCCESS') {
          yield put(scrapeRestaurantInfoSuccess())
          break;
        }

        break;

      case websocketEmitterType.update:
        yield put(getBookingList(currentRestaurant, date));

        yield call(refetchActiveBookingsQueries, {
          type: `WEBSOCKET_${event.type}`,
          date,
          event,
        });

        if (['CANCELED'].indexOf(event.payload.bookingState) !== -1) {
          yield put(outerChangeTypeAppReserve('CANCELED', event.payload.id));
          yield put(removeOpenReserve(event.payload.id));
        }

        break;

      case websocketEmitterType.newCall:
        yield put(addRestaurantCall(event.payload));
        break;

      case websocketEmitterType.callEnded:
        yield put(removeRestaurantCall(event.payload.id, false));
        break;

      case SET_WHATSAPP_RESTAURANT_STATE:
        yield put(setRestaurantWhatsappState(event.payload));
        break;

      case SET_TG_RESTAURANT_STATE:
        yield put(setRestaurantTgState(event.payload));
        break;

      case websocketEmitterType.premiumBonusStatus:
        yield put(changePremiumBonusStatus(event.payload));
        break;

      default:
        break;
    }
  }
}

export function* websocketSaga({ type }: IInitStarting) {
  const isAuthorized = yield select(selectorIsAuthorized);

  if (type !== INIT_START || !isAuthorized) {
    try {
      // make a query, if user is not authorized, token renewal mechanic
      // will be triggered.
      yield queryWithRetry(Api.getAccountReq, {}, Infinity, 5000);
    } catch (err) {
      const isCancelled = yield cancelled();

      if (isCancelled) {
        return false;
      }
      if (err && err.response && String(err.response.status) === '404') {
        return false;
      }
      yield delay(5000);
      yield put(socketReconnect());
      return false;
    }
  }

  const channel = yield call(createSocketChannel);

  try {
    return yield watchChannelEvents(channel);
  } finally {
    if (yield cancelled()) {
      channel.close();
    }
  }
}

function sendSocketMessageSaga(action: any) {
  try {
    const { destination, payload, headers } = action.payload || {};
    if (!destination) {
      console.warn("SOCKET_SEND_MESSAGE without destination");
      return;
    }

    if (stompClient) {
      const token = getAccessToken();
      const body = typeof payload === "string" ? payload : JSON.stringify(payload);

      stompClient.send(
        destination,
        body,
        {
          ...(headers || {}),
          Authorization: `Bearer ${token}`,
        },
      );

      return;
    }

    // @todo Если сокет не подключен, то можно реализовать
    //   очередь в ожидании подключения
  } catch (e) {
    console.error("sendSocketMessageSaga failed", e);
  }
}

function handleSubscribeMessengerAuth() {
  wantMessengerAuth = true;

  if (stompClient?.connected && !messengerAuthSub) {
    messengerAuthSub = stompClient.subscribe(
      MESSENGER_AUTH_TOPIC,
      (msg: any) => guestmeNotificationsGatewayCallback(msg, socketEmitterRef),
    );
  }
}

function handleUnsubscribeMessengerAuth() {
  wantMessengerAuth = false;
  if (messengerAuthSub) {
    try {
      messengerAuthSub.unsubscribe();
    } catch (e) {
      console.log('unsubscribe error', e)
    }
    messengerAuthSub = null;
  }
}

export default function* saga() {
  yield all([
    takeLatest([INIT_START, SOCKET_RECONNECT], websocketSaga),

    takeEvery(SOCKET_SUBSCRIBE_MESSENGER_AUTH, handleSubscribeMessengerAuth),
    takeEvery(SOCKET_UNSUBSCRIBE_MESSENGER_AUTH, handleUnsubscribeMessengerAuth),

    takeEvery([SOCKET_SEND_MESSAGE], sendSocketMessageSaga),
  ]);
}
